import { useEffect, useRef, useState } from 'react';

const services = [
  {
    icon: 'ri-box-3-line',
    badge: '팩 공급',
    title: '플라스틱 팩',
    subtitle: '과일별 최적화 용기',
    desc: '토마토, 블루베리, 딸기, 포도 등 과일 특성에 맞는 플라스틱 팩을 빠르게 공급합니다. 소량부터 대량까지 유연하게 대응합니다.',
    items: ['투명 플라스틱 팩', '뚜껑 일체형 용기', '통기 구멍 타입', '소분 포장용 팩'],
    color: '#0A508C',
    lightColor: '#E8F1FA',
  },
  {
    icon: 'ri-price-tag-3-line',
    badge: '라벨',
    title: '맞춤 라벨 디자인',
    subtitle: '브랜드를 담은 라벨',
    desc: '농가 브랜드 혹은 출하 정보를 담은 맞춤형 라벨을 디자인·제작합니다. 깔끔하고 선명한 인쇄로 상품의 신뢰도를 높입니다.',
    items: ['브랜드 로고 반영', '원산지·품종 표기', '바코드/QR 인쇄', '소량 주문 가능'],
    color: '#3A7CC0',
    lightColor: '#EAF2FA',
  },
  {
    icon: 'ri-pushpin-2-line',
    badge: '부착',
    title: '라벨 부착 서비스',
    subtitle: '받는 즉시 출하 준비',
    desc: '제작된 라벨을 팩에 정확하게 부착하여 납품합니다. 받는 즉시 판매 가능한 완성 상태로 출고되어 농가의 수고를 줄여줍니다.',
    items: ['팩 + 라벨 일괄 처리', '납품 전 품질 확인', '빠른 처리 일정', '대량 부착 가능'],
    color: '#2BC0D0',
    lightColor: '#E6F9FB',
  },
];

export default function ServicesSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="services" ref={ref} className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className={`flex flex-col lg:flex-row lg:items-end lg:justify-between mb-14 gap-4 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <div>
            <div className="text-sm font-bold uppercase tracking-widest mb-3" style={{ color: '#2BC0D0' }}>CORE SERVICES</div>
            <h2 className="text-4xl font-black text-gray-900 leading-tight">
              신진패키징의<br className="hidden sm:block" /> 핵심 서비스
            </h2>
          </div>
          <p className="text-base text-gray-500 max-w-sm leading-relaxed">
            패키지 조달부터 라벨 제작, 부착까지<br />
            농산물 출하에 필요한 모든 것을 한 곳에서
          </p>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {services.map((svc, i) => (
            <div
              key={svc.title}
              className={`rounded-2xl p-7 border border-gray-100 transition-all duration-700 hover:-translate-y-1 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: `${i * 120}ms`, backgroundColor: svc.lightColor }}
            >
              {/* Badge */}
              <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-bold text-white mb-5" style={{ background: svc.color }}>
                {svc.badge}
              </div>

              {/* Icon */}
              <div className="w-14 h-14 flex items-center justify-center rounded-xl mb-5" style={{ background: svc.color }}>
                <i className={`${svc.icon} text-white text-2xl`} />
              </div>

              {/* Title */}
              <h3 className="text-xl font-black text-gray-900 mb-1">{svc.title}</h3>
              <div className="text-sm font-semibold mb-3" style={{ color: svc.color }}>{svc.subtitle}</div>

              {/* Desc */}
              <p className="text-base text-gray-600 leading-relaxed mb-5 break-keep tracking-tight">{svc.desc}</p>

              {/* Items */}
              <ul className="flex flex-col gap-2.5">
                {svc.items.map((item) => (
                  <li key={item} className="flex items-center gap-2.5 text-base text-gray-700">
                    <span className="w-5 h-5 flex items-center justify-center rounded-full flex-shrink-0" style={{ background: svc.color }}>
                      <i className="ri-check-line text-white text-xs" />
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom Banner */}
        <div
          className={`mt-10 rounded-2xl p-6 flex flex-col md:flex-row items-center justify-between gap-4 transition-all duration-700 delay-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          style={{ background: 'linear-gradient(135deg, #0A508C, #2BC0D0)' }}
        >
          <div className="text-white">
            <div className="font-black text-lg">팩 + 라벨 + 부착, 세 가지를 한 번에 주문하세요</div>
            <div className="text-white/70 text-base mt-1">별도 업체 찾는 시간과 비용을 절약할 수 있습니다</div>
          </div>
          <a
            href="https://smartstore.naver.com"
            target="_blank"
            rel="nofollow noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 bg-white rounded-xl font-bold text-base cursor-pointer whitespace-nowrap transition-opacity hover:opacity-90 flex-shrink-0"
            style={{ color: '#0A508C' }}
          >
            스마트스토어 바로가기
            <i className="ri-external-link-line" />
          </a>
        </div>
      </div>
    </section>
  );
}
